import { useEffect, useState } from "react";
import { track, useCount } from "./analytics";
import type { View } from "./nav";
import { Mascot, Sheet } from "./ui";

/**
 * After some real use (a few successful remote actions), ask once how Brocoded is going: 1 to 5 stars. Not asked during the first-pairing alerts prompt, never again after an
 * answer, and a dismissal waits a few weeks. A star is recorded at once (a low one then asks for an optional note); before rating, only the cross closes the card.
 */
const KEY = "bc.review"; // "done" | the time of the last dismissal
const MIN_USES = 1;
const WAIT_MS = 21 * 86400_000;
const RELAY_HTTP: string = (import.meta.env.VITE_RELAY_URL ?? "").replace(/\/+$/, "");
const MAIN = new Set<View["name"]>(["sessions", "home", "computers", "inbox"]);

const read = (k: string): string | null => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (v: string) => {
  try {
    localStorage.setItem(KEY, v);
  } catch {
    /* no storage: asked again next load, which is fine */
  }
};
function due(): boolean {
  if (read("foreman.alerts.ask") === "1") return false; // the alerts question comes first
  const last = read(KEY);
  if (last === "done") return false;
  if (last && Date.now() - Number(last) < WAIT_MS) return false;
  return useCount() >= MIN_USES;
}

export function ReviewAsk({ view }: { view: View }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [thanks, setThanks] = useState(false);
  const [note, setNote] = useState("");
  const main = MAIN.has(view.name);
  useEffect(() => {
    if (open || !main || !due()) return;
    const t = setTimeout(() => {
      setOpen(true);
      track("review_prompt_shown", { uses: useCount() });
    }, 2500);
    return () => clearTimeout(t);
  }, [main, open]);
  if (!open) return null;

  const dismiss = () => {
    if (!rating) {
      write(String(Date.now()));
      track("review_prompt_dismissed");
    }
    setOpen(false);
  };
  const thank = () => {
    setThanks(true);
    setTimeout(() => setOpen(false), 1500);
  };
  // A star is the answer: it is recorded at once. A good one is thanked and the card closes by itself; a low one asks what went wrong.
  const rate = (n: number) => {
    if (rating) return;
    setRating(n);
    write("done");
    track("review_submitted", { rating: n });
    if (RELAY_HTTP) {
      const ua = navigator.userAgent;
      const platform = /iPhone|iPad|iPod/.test(ua) ? "ios" : /Android/.test(ua) ? "android" : /Mac/.test(ua) ? "mac" : /Windows/.test(ua) ? "windows" : "other";
      fetch(`${RELAY_HTTP}/review`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ rating: n, version: String(import.meta.env.VITE_APP_VERSION ?? "dev"), platform }) }).catch(() => undefined);
    }
    if (n > 3) thank();
  };
  const sendNote = () => {
    if (note.trim().length >= 5 && RELAY_HTTP) {
      track("review_note_sent", { rating });
      fetch(`${RELAY_HTTP}/support`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ message: `[Review ${rating}/5] ${note.trim()}`, contact: "", topic: "other", version: String(import.meta.env.VITE_APP_VERSION ?? "dev"), platform: "Review", diag: "" }),
      }).catch(() => undefined);
    }
    thank();
  };
  const noting = rating > 0 && rating <= 3 && !thanks;

  return (
    // Tapping outside closes it only once a rating is in; before that, only the cross does.
    <Sheet onClose={() => (rating ? dismiss() : undefined)} top center>
      <div className="gate-modal review-card" data-review-ask>
        {!thanks && (
          <button className="review-close" aria-label="Close" onClick={dismiss}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
        <Mascot size={72} />
        {thanks ? (
          <div className="t" role="status">
            Thank you
          </div>
        ) : (
          <>
            <div className="t">{noting ? "Sorry to hear that" : "How is Brocoded working for you?"}</div>
            <p className="fine">{noting ? "What went wrong? It goes straight to us." : "It helps us decide what to fix next."}</p>
            <div className="review-stars" role="radiogroup" aria-label="Rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} role="radio" aria-checked={rating === n} aria-label={`${n} of 5`} className={"review-star" + (n <= rating ? " on" : "")} disabled={rating > 0} onClick={() => rate(n)}>
                  ★
                </button>
              ))}
            </div>
            {noting && (
              <>
                <textarea className="review-note" rows={3} maxLength={500} autoFocus placeholder="Tell us in a few words (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
                <button className="btn primary" onClick={sendNote}>
                  Send
                </button>
              </>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}
